/**
 * Document Generate page.
 * Allows generating PDFs from a document using recipes, and lists generated documents.
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useDocument } from "../hooks/useDocuments";
import {
  useGenerateDocumentPdf,
  useDocumentGeneratedDocuments,
  useDeleteDocumentGeneratedDocument,
} from "../hooks/useDocuments";
import { useProject } from "../hooks/useProjects";
import { useWorkspaceUrl } from "../lib/routes";
import { listWorkspaceRecipes, InsufficientBoostsError } from "../services/naraDoor";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";
import BoostGateError from "../components/BoostGateError";
import { useProjectTemplate } from "../hooks/useProjectTemplates";
import { PageHeader } from "../components/layout/PageHeader";
import { KiiConfirmDialog } from "@/components/kii/kii-confirm-dialog";
import {
  FileDown,
  Download,
  Trash2,
  Loader2,
  AlertCircle,
  Sparkles,
  RefreshCw,
} from "lucide-react";

export function DocumentGenerate() {
  const { t } = useTranslation();
  const { projectId, id: documentId } = useParams<{
    projectId: string;
    id: string;
  }>();
  const urls = useWorkspaceUrl();

  // Data
  const { data: document, isLoading: documentLoading } = useDocument(projectId, documentId);
  const { data: project } = useProject(projectId);
  const { data: projectTemplate } = useProjectTemplate(project?.template_id);
  const generatePdf = useGenerateDocumentPdf();
  const { data: generatedDocs, isError: generatedError } = useDocumentGeneratedDocuments(projectId, documentId);
  const deleteMutation = useDeleteDocumentGeneratedDocument(projectId, documentId);

  // Recipes
  const workspaceId = getCurrentWorkspaceId();
  const {
    data: recipesData,
    isLoading: recipesLoading,
    isError: recipesError,
    error: recipesErrorDetail,
    refetch: refetchRecipes,
  } = useQuery({
    queryKey: ["transcriptionRecipes", workspaceId],
    queryFn: () => listWorkspaceRecipes(workspaceId!, "transcription"),
    enabled: !!workspaceId,
  });
  const recipes = recipesData?.recipes || [];

  useEffect(() => {
    if (recipesError) {
      console.error("Failed to load recipes:", (recipesErrorDetail as Error)?.message);
    }
  }, [recipesError, recipesErrorDetail]);

  // State
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Auto-select default recipe from project template
  useEffect(() => {
    if (recipes.length > 0 && !selectedRecipeId) {
      const defaultId = projectTemplate?.default_transcription_recipe_id;
      if (defaultId && recipes.some((r: any) => r.id === defaultId)) {
        setSelectedRecipeId(defaultId);
      } else {
        setSelectedRecipeId(recipes[0].id);
      }
    }
  }, [recipes, selectedRecipeId, projectTemplate]);

  const handleGenerate = async () => {
    if (!projectId || !documentId || !selectedRecipeId || generatePdf.isPending) return;
    try {
      await generatePdf.mutateAsync({ projectId, documentId, recipeId: selectedRecipeId });
    } catch {
      // error handled by mutation state
    }
  };

  const handleRegenerate = async (recipeId: string) => {
    if (!projectId || !documentId || generatePdf.isPending) return;
    try {
      await generatePdf.mutateAsync({ projectId, documentId, recipeId });
    } catch {
      // error handled by mutation state
    }
  };

  const handleDelete = (generatedDocumentId: string) => {
    setDeleteTarget(generatedDocumentId);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate({ generatedDocumentId: deleteTarget });
      setDeleteTarget(null);
    }
  };

  if (documentLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted rounded animate-pulse" />
        <div className="h-96 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t("documentGenerate.title", "Generate Document")}
        breadcrumbs={[
          { label: "Projects", href: urls?.projects },
          {
            label: project?.name || "Project",
            href: urls && projectId ? urls.project(projectId) : undefined,
          },
          {
            label: document?.name || "Document",
            href: urls && projectId && documentId ? urls.document(projectId, documentId) : undefined,
          },
          { label: t("documentGenerate.title", "Generate Document") },
        ]}
      />

      <div className="space-y-6 max-w-2xl">
        {/* Recipe Picker */}
        <div className="bg-card rounded-lg border border-border p-4">
          <h3 className="font-medium text-foreground mb-3">
            {t("documentGenerate.selectRecipe", "Select Recipe")}
          </h3>
          {recipesLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("documentGenerate.loadingRecipes", "Loading recipes...")}
            </div>
          ) : recipesError ? (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {t("documentGenerate.loadRecipesError", "Couldn’t load recipes. Try again.")}
                </p>
                <button
                  onClick={() => refetchRecipes()}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300 hover:underline"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t("documentGenerate.retry", "Retry")}
                </button>
              </div>
            </div>
          ) : recipes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("documentGenerate.noRecipes", "No recipes available.")}
            </p>
          ) : (
            <div className="space-y-2">
              {recipes.map((recipe: any) => (
                <label
                  key={recipe.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedRecipeId === recipe.id
                      ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/30"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  <input
                    type="radio"
                    name="recipe"
                    value={recipe.id}
                    checked={selectedRecipeId === recipe.id}
                    onChange={() => setSelectedRecipeId(recipe.id)}
                    className="text-blue-600"
                  />
                  <div>
                    <div className="font-medium text-sm text-foreground">{recipe.name}</div>
                    {recipe.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">{recipe.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={!selectedRecipeId || generatePdf.isPending || recipesError}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
        >
          {generatePdf.isPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("documentGenerate.generating", "Generating...")}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              {t("documentGenerate.generate", "Generate Document")}
            </>
          )}
        </button>

        {generatePdf.error instanceof InsufficientBoostsError && <BoostGateError error={generatePdf.error} />}

        {generatePdf.isError && !(generatePdf.error instanceof InsufficientBoostsError) && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-700 dark:text-red-300">
              {generatePdf.error?.message?.includes("resource-exhausted")
                ? t("documentGenerate.errorRateLimit", "Service is temporarily busy. Please wait a moment and try again.")
                : t("documentGenerate.error", "Failed to generate document. Please try again.")}
            </p>
          </div>
        )}

        {generatedError && <p className="text-sm text-destructive">{t("generatedDocuments.loadError")}</p>}
        {deleteMutation.isError && <p className="text-sm text-destructive">{t("generatedDocuments.deleteError")}</p>}

        {/* Generated Documents List */}
        {generatedDocs && generatedDocs.documents.length > 0 && (
          <div className="bg-card rounded-lg border border-border p-4">
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              {t("documentGenerate.generatedDocuments", "Generated Documents")}
            </h3>
            <div className="space-y-2">
              {generatedDocs.documents.map((doc) => {
                const isActive = doc.status && !["completed", "failed"].includes(doc.status);
                const isFailed = doc.status === "failed";
                const isCompleted = !doc.status || doc.status === "completed";

                // Find recipe name
                const recipeName = recipes.find((r: any) => r.id === doc.template_used)?.name;

                return (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                      isFailed
                        ? "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30"
                        : isActive
                        ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-foreground truncate">{doc.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2">
                        {doc.created_at && new Date(doc.created_at).toLocaleString()}
                        {isCompleted && doc.file_size_bytes && (
                          <span>({doc.file_size_bytes >= 1048576 ? `${(doc.file_size_bytes / 1048576).toFixed(1)} MB` : `${(doc.file_size_bytes / 1024).toFixed(1)} KB`})</span>
                        )}
                        {recipeName && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px]">
                            {recipeName}
                          </span>
                        )}
                        {isActive && (
                          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {doc.status === "pending" && t("documentGenerate.statusPending", "Starting...")}
                            {doc.status === "generating" && (
                              doc.status_detail || t("documentGenerate.statusGenerating", "Generating...")
                            )}
                            {doc.status === "rendering" && t("documentGenerate.statusRendering", "Rendering PDF...")}
                          </span>
                        )}
                        {isFailed && (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title={doc.error_message || ""}>
                            <AlertCircle className="h-3 w-3" />
                            {t("documentGenerate.statusFailed", "Generation failed")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isCompleted && doc.download_url && (
                        <a
                          href={doc.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 rounded-lg transition-colors"
                          title={t("documentGenerate.download", "Download")}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      )}
                      {isCompleted && doc.template_used && (
                        <button
                          onClick={() => handleRegenerate(doc.template_used!)}
                          disabled={generatePdf.isPending}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                          title={t("documentGenerate.regenerate", "Regenerate")}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors disabled:opacity-50"
                        title={t("documentGenerate.delete", "Delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <KiiConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t("documentGenerate.deleteTitle", "Delete Generated Document")}
        description={t("documentGenerate.deleteDescription", "This will permanently delete this generated document. This action cannot be undone.")}
        confirmText={t("documentGenerate.deleteConfirm", "Delete")}
        cancelText={t("documentGenerate.deleteCancel", "Cancel")}
        variant="destructive"
      />
    </div>
  );
}
